<script setup lang="ts">
import { computed, ref } from 'vue';
import { useI18n } from 'vue-i18n';
import { Components } from '@/api/generated/api';
import RatingEmoji from './RatingEmoji.vue';
import RatingStarsInput from './RatingStarsInput.vue';
import CustomButton from '@/core/elements/CustomButton.vue';
import DigidooAvatar from '@/assets/DigidooAvatar.vue';

const props = defineProps<{
  entity?: Components.Schemas.UserFeedbackEntity;
  entityCount?: number;
}>();

const selectedStar = ref<number>(0);
const hoveredStar = ref<number | null>(null);

const emit = defineEmits<{
  (event: 'submit-star-rating', value: number): void;
}>();

const { t } = useI18n();

const entityLabel = computed((): string => {
  if (!props.entity) return '';
  return t(`help.rating.entities.${props.entity}`);
});

const isAutomatic = computed((): boolean => {
  return props.entity !== undefined && props.entityCount !== undefined;
});

const handleUpdateStar = async (newRating: number): Promise<void> => {
  selectedStar.value = newRating;
};

const handleUpdateHoveredStar = (star: number | null): void => {
  hoveredStar.value = star;
};

const resetState = (): void => {
  selectedStar.value = 0;
};

defineExpose({
  resetState,
});
</script>

<template>
  <div v-if="isAutomatic" class="flex flex-col justify-center gap-y-xl">
    <DigidooAvatar class="w-[80px] h-[80px] mx-auto" />
    <div class="flex flex-col items-center">
      <span class="font-semibold leading-[100px] text-[100px]">
        {{ props.entityCount }}
      </span>
      <span class="text-title">
        {{ entityLabel }}
      </span>
    </div>
    <p class="leading-[27px] text-body text-center">
      {{
        t('help.rating.feedbackEntityDescription', {
          count: props.entityCount,
          entity: entityLabel,
        })
      }}
    </p>
  </div>
  <div v-else class="flex flex-col justify-center gap-y-xl">
    <RatingEmoji :level="hoveredStar ?? selectedStar ?? 4" class="w-[80px] h-[80px] mx-auto" />
    <h1 class="text-title leading-[36px] font-medium text-center">
      {{ t('help.rating.title') }}
    </h1>
    <p class="text-body leading-[27px] font-normal text-center">
      {{ t('help.rating.description') }}
    </p>
  </div>

  <div class="flex flex-col gap-y-xl mt-xl">
    <RatingStarsInput
      :rating="selectedStar"
      @update:clicked="handleUpdateStar"
      @update:hovered="handleUpdateHoveredStar"
    />
    <CustomButton
      v-if="selectedStar"
      type="button"
      size="lg"
      theme="primary"
      class="mx-auto w-full flex items-center justify-center"
      @click="emit('submit-star-rating', selectedStar)"
    >
      {{ t('help.rating.rateNow') }}
    </CustomButton>
  </div>
</template>
